import React, { useState, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ShoppingCart, Minus, Plus, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useShop, API } from "../context/ShopContext";
import { useLocale } from "../context/LocaleContext";
import { formatPrice } from "../mock";
import ProductCard from "../components/ProductCard";
import Seo from "../components/Seo";

const ProductDetail = () => {
  const { slug } = useParams();
  const { t, lang, addToCart, products, categories, loading } = useShop();
  const { lp, registerAltUrls } = useLocale();
  const [qty, setQty] = useState(1);
  const [activeTab, setActiveTab] = useState("description");
  const [docHtml, setDocHtml] = useState(null);
  const [docLoading, setDocLoading] = useState(false);

  // Lo slug in URL puo' essere IT (radice) o EN (/en/product/<slug_en>).
  const product = !loading ? products.find((p) => p.slug === slug || p.slug_en === slug) : null;

  useEffect(() => {
    const hasDoc = product?.doc_file_it || product?.doc_file_en;
    if (activeTab === "documentation" && hasDoc) {
      setDocHtml(null);
      setDocLoading(true);
      fetch(`${API}/products/${product.slug}/doc?lang=${lang}`)
        .then((r) => r.text())
        .then((html) => setDocHtml(html))
        .catch(() => setDocHtml("<p>Documentazione non disponibile.</p>"))
        .finally(() => setDocLoading(false));
    }
  }, [activeTab, product, lang]);

  // URL alternati reali (it/en) per hreflang + language switcher.
  useEffect(() => {
    if (product) {
      registerAltUrls(`/product/${product.slug}`, `/en/product/${product.slug_en || product.slug}`);
    }
  }, [product, registerAltUrls]);

  if (loading) {
    return (
      <div className="idm-loading idm-loading-page" data-testid="loading">
        <span className="idm-spinner" />
      </div>
    );
  }

  if (!product) return <Navigate to={lp("/shop")} replace />;

  const prodCats = categories.filter((c) => product.categories.includes(c.slug));
  const related = products
    .filter(
      (p) =>
        p.id !== product.id &&
        p.categories.some((c) => product.categories.includes(c))
    )
    .slice(0, 3);

  const handleAdd = () => {
    addToCart(product, qty);
    toast.success(t("addedToCart"), { description: `${qty} × ${product.name[lang]}` });
  };

  return (
    <div data-testid="product-detail-page">
      <Seo
        title={product.name[lang]}
        description={product.shortDescription[lang]}
        image={product.image}
        type="product"
      />
      <div className="idm-breadcrumb-bar">
        <div className="idm-container idm-breadcrumb">
          <Link to={lp("/")}>{t("home")}</Link>
          <ChevronRight size={13} />
          <Link to={lp("/shop")}>{t("shop")}</Link>
          <ChevronRight size={13} />
          <span>{product.name[lang]}</span>
        </div>
      </div>

      <div className="idm-container idm-pd-layout">
        <div className="idm-pd-image">
          {product.onSale && <span className="idm-sale-badge">{t("sale")}</span>}
          <img src={product.image} alt={product.name[lang]} />
        </div>

        <div className="idm-pd-info">
          <h1 className="idm-pd-title" data-testid="product-title">{product.name[lang]}</h1>
          <div className="idm-pd-price" data-testid="product-price">
            {product.onSale && (
              <span className="idm-price-old">{formatPrice(product.regularPrice)}</span>
            )}
            <span className="idm-price">{formatPrice(product.price)}</span>
            {product.priceSuffix && (
              <span className="idm-price-suffix"> {product.priceSuffix[lang]}</span>
            )}
          </div>
          <p className="idm-pd-short">{product.shortDescription[lang]}</p>

          <div className="idm-pd-actions">
            <div className="idm-qty" data-testid="qty-selector">
              <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-minus" aria-label="Decrease">
                <Minus size={14} />
              </button>
              <span data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} data-testid="qty-plus" aria-label="Increase">
                <Plus size={14} />
              </button>
            </div>
            <button className="idm-btn-yellow" onClick={handleAdd} data-testid="pd-add-to-cart">
              <ShoppingCart size={16} />
              {t("addToCart")}
            </button>
          </div>

          <div className="idm-pd-meta">
            <p>
              <strong>{t("sku")}:</strong> {product.sku}
            </p>
            <p>
              <strong>{prodCats.length > 1 ? t("categories") : t("category")}:</strong>{" "}
              {prodCats.map((c, i) => (
                <React.Fragment key={c.slug}>
                  {i > 0 && ", "}
                  <Link to={`${lp("/shop")}?cat=${c.slug}`}>{c.name[lang]}</Link>
                </React.Fragment>
              ))}
            </p>
            {product.tags?.length > 0 && (
              <p data-testid="product-tags">
                <strong>{t("tags")}:</strong> {product.tags.join(", ")}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="idm-container idm-pd-desc-wrap">
        <div className="idm-pd-tab-head">
          <span
            className={activeTab === "description" ? "active" : ""}
            onClick={() => setActiveTab("description")}
            style={{ cursor: "pointer" }}
          >
            {t("description")}
          </span>
          {(product.doc_file_it || product.doc_file_en) && (
            <span
              className={activeTab === "documentation" ? "active" : ""}
              onClick={() => setActiveTab("documentation")}
              style={{ cursor: "pointer" }}
              data-testid="tab-documentation"
            >
              {t("documentation")}
            </span>
          )}
        </div>
        {activeTab === "description" && (
          <div className="idm-pd-desc" data-testid="product-description">
            {product.description[lang].split("\n").map((line, i) =>
              line.trim() === "" ? <br key={i} /> : <p key={i}>{line}</p>
            )}
          </div>
        )}
        {activeTab === "documentation" && (
          <div className="idm-pd-desc" data-testid="product-documentation">
            {docLoading ? (
              <div className="idm-loading"><Loader2 size={20} className="idm-spin" /></div>
            ) : (
              <div dangerouslySetInnerHTML={{ __html: docHtml || "" }} />
            )}
          </div>
        )}
      </div>

      {related.length > 0 && (
        <div className="idm-container idm-related">
          <h2 className="idm-section-title">{t("relatedProducts")}</h2>
          <span className="idm-separator" />
          <div className="idm-product-grid">
            {related.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
